// T-277 (collettiva): INBOX richieste di fornitura lato RESPONSABILE.
// Le richieste le crea il PREPOSTO esterno (PrepostoOrderPage) → qui il
// responsabile le vede in sola lettura, apre il dettaglio (full-screen, comandi
// in testa), Accetta/Rifiuta, e va a "Pianifica produzione". NON crea richieste.
import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { arrowForward, checkmarkCircleOutline, closeCircleOutline, closeOutline, restaurantOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { Destination, SupplyRequest, SupplyRequestStatus } from '@trackeat/shared';
import { SUPPLY_REQUEST_STATUS_LABELS, SUPPLY_REQUEST_MEAL_SERVICE_LABELS, supplyRequestTotalMeals } from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useCollettivaEnabled, useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';

const BUCKET = 'supply-request-attachments';

type SupplyRequestRow = Omit<SupplyRequest, 'version' | 'created_at' | 'archived_at'> & {
  version?: number;
  created_at?: string;
  archived_at?: string | null;
};
type DestinationRow = Pick<Destination, 'id' | 'name' | 'archived_at'> & { archived_at?: string | null };

const STATUS_COLORS: Record<SupplyRequestStatus, { bg: string; fg: string }> = {
  draft: { bg: '#f1f5f9', fg: '#475569' },
  submitted: { bg: '#e0f2fe', fg: '#0369a1' },
  accepted: { bg: '#dcfce7', fg: '#15803d' },
  in_production: { bg: '#fef3c7', fg: '#b45309' },
  fulfilled: { bg: '#ede9fe', fg: '#6d28d9' },
  rejected: { bg: '#fee2e2', fg: '#b91c1c' },
};

export function SupplyRequestsPage(): JSX.Element {
  const { profile } = useAuth();
  const history = useHistory();
  const collettivaEnabled = useCollettivaEnabled();
  const { data: requests = [] } = useMasters<SupplyRequestRow>('supply_requests');
  const { data: destinations = [] } = useMasters<DestinationRow>('destinations');
  const enqueue = useEnqueueMaster();
  const sb = getSupabase();

  const [detail, setDetail] = useState<SupplyRequestRow | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<SupplyRequestRow | null>(null);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const destById = useMemo(() => {
    const m = new Map<string, string>();
    destinations.forEach((d) => m.set(d.id, d.name));
    return m;
  }, [destinations]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests
      .filter((r) => !r.archived_at)
      // T-436: le richieste in 'draft' sono ordini per-letto ancora IN CONTROLLO
      // dal preposto (non ancora inviati al centro cottura) → la cucina NON le
      // deve vedere finché il preposto non le verifica e invia (→ 'submitted').
      .filter((r) => r.status !== 'draft')
      .filter((r) => {
        if (!q) return true;
        const dn = (destById.get(r.destination_id) ?? '').toLowerCase();
        return dn.includes(q) || (r.requested_for_date ?? '').includes(q);
      })
      .sort((a, b) => (b.requested_for_date ?? '').localeCompare(a.requested_for_date ?? ''));
  }, [requests, search, destById]);
  const nSubmitted = visible.filter((r) => r.status === 'submitted').length;

  // Raggruppa per giorno di servizio (header data); submitted in cima per giorno.
  const byDay = useMemo(() => {
    const m = new Map<string, SupplyRequestRow[]>();
    for (const r of visible) {
      const k = r.requested_for_date ?? '—';
      (m.get(k) ?? m.set(k, []).get(k)!).push(r);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.status === 'submitted' ? 0 : 1) - (b.status === 'submitted' ? 0 : 1));
    }
    return [...m.entries()]; // già ordinati per data desc da `visible`
  }, [visible]);

  // Signed URL per gli allegati della richiesta aperta.
  useEffect(() => {
    if (!detail) return;
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const p of detail.attachment_paths) {
        if (signed[p]) continue;
        try {
          const { data } = await sb.storage.from(BUCKET).createSignedUrl(p, 60 * 15);
          if (data?.signedUrl) next[p] = data.signedUrl;
        } catch {
          /* anteprima non critica */
        }
      }
      if (!cancelled && Object.keys(next).length > 0) setSigned((s) => ({ ...s, ...next }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  async function setStatus(r: SupplyRequestRow, status: SupplyRequestStatus): Promise<void> {
    setBusy(true);
    try {
      await enqueue.mutateAsync({
        table: 'supply_requests',
        id: r.id,
        op: 'update',
        base_version: r.version ?? 0,
        patch: {
          id: r.id,
          tenant_id: r.tenant_id,
          status,
          ...(status === 'accepted'
            ? { accepted_at: new Date().toISOString(), accepted_by: profile?.id ?? null }
            : {}),
        },
      });
      setToast(status === 'accepted' ? 'Richiesta accettata' : status === 'rejected' ? 'Richiesta rifiutata' : 'Aggiornata');
      setDetail(null);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function archive(r: SupplyRequestRow): Promise<void> {
    try {
      await enqueue.mutateAsync({ table: 'supply_requests', id: r.id, op: 'archive', base_version: r.version ?? 0, patch: { id: r.id } });
      setToast('Richiesta archiviata');
      setConfirmArchive(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>Richieste di fornitura</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {!collettivaEnabled && (
          <div style={{ padding: '24px 16px' }}>
            <p style={{ fontSize: 14, color: '#475569' }}>Modulo collettiva non attivo per questo locale.</p>
          </div>
        )}
        {collettivaEnabled && (
          <div style={{ padding: '12px 16px 96px' }}>
            <p style={{ fontSize: 13, color: '#475569', margin: '4px 4px 12px' }}>
              Gli ordini in arrivo dalle destinazioni. Accetta quelli da produrre, poi vai a pianificare la produzione.
            </p>

            <IonButton expand="block" onClick={() => history.push('/warehouse/production-plan')} style={{ marginBottom: 16 }}>
              Pianifica produzione
              <IonIcon icon={arrowForward} slot="end" />
            </IonButton>

            <IonSearchbar
              value={search}
              placeholder="Cerca per destinazione o data…"
              onIonInput={(e) => setSearch(String(e.detail.value ?? ''))}
              style={{ '--background': '#fff', '--border-radius': '12px', paddingInline: 0, marginBottom: 4 } as React.CSSProperties}
            />
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 12px' }}>
              In arrivo · {nSubmitted} da decidere / {visible.length} totali
            </h2>

            {visible.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
                <p style={{ margin: 0, fontStyle: 'italic' }}>{search ? 'Nessuna richiesta trovata.' : 'Nessuna richiesta. Gli ordini arrivano dalle destinazioni (preposti).'}</p>
              </div>
            ) : (
              byDay.map(([day, rows]) => (
                <div key={day} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', margin: '8px 4px 6px' }}>📅 {day}</div>
                  {rows.map((r) => {
                    const c = STATUS_COLORS[r.status];
                    const meals = supplyRequestTotalMeals(r.lines);
                    return (
                      <div
                        key={r.id}
                        onClick={() => setDetail(r)}
                        role="button"
                        style={{
                          background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
                          boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                        }}
                      >
                        <div style={{ width: 40, height: 40, borderRadius: 20, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <IonIcon icon={restaurantOutline} style={{ fontSize: 20, color: c.fg }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{destById.get(r.destination_id) ?? 'Destinazione'}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', background: c.bg, color: c.fg, borderRadius: 999, padding: '2px 8px' }}>
                              {SUPPLY_REQUEST_STATUS_LABELS[r.status]}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            {meals} pasti{r.attachment_paths.length > 0 ? ` · ${r.attachment_paths.length} foto` : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {/* Dettaglio richiesta — full-screen, comandi (Accetta/Rifiuta) IN TESTA */}
        <IonModal isOpen={!!detail} onDidDismiss={() => setDetail(null)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setDetail(null)} aria-label="chiudi">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{detail ? (destById.get(detail.destination_id) ?? 'Richiesta') : ''}</IonTitle>
              {detail?.status === 'submitted' && (
                <IonButtons slot="end">
                  <IonButton color="danger" disabled={busy} onClick={() => detail && void setStatus(detail, 'rejected')}>
                    <IonIcon icon={closeCircleOutline} slot="start" />
                    Rifiuta
                  </IonButton>
                  <IonButton color="success" disabled={busy} onClick={() => detail && void setStatus(detail, 'accepted')}>
                    <IonIcon icon={checkmarkCircleOutline} slot="start" />
                    Accetta
                  </IonButton>
                </IonButtons>
              )}
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {detail && (
              <>
                <IonItem lines="none">
                  <IonLabel>
                    <h2 style={{ fontWeight: 700 }}>{detail.requested_for_date}</h2>
                    <p>
                      {SUPPLY_REQUEST_STATUS_LABELS[detail.status]} · {supplyRequestTotalMeals(detail.lines)} pasti
                    </p>
                  </IonLabel>
                  <IonBadge slot="end" style={{ background: STATUS_COLORS[detail.status].bg, color: STATUS_COLORS[detail.status].fg }}>
                    {SUPPLY_REQUEST_STATUS_LABELS[detail.status]}
                  </IonBadge>
                </IonItem>

                {detail.status === 'submitted' ? (
                  <div style={{ background: '#fff', borderRadius: 12, padding: 16, color: '#64748b', fontSize: 13, marginTop: 8 }}>
                    🔒 I piatti richiesti si vedono dopo aver <strong>accettato</strong> la richiesta (usa <strong>Accetta</strong> in alto).
                  </div>
                ) : (
                  <>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '14px 4px 4px', textTransform: 'uppercase' }}>Piatti richiesti</h3>
                    {detail.lines.length === 0 ? (
                      <IonNote style={{ padding: '0 4px' }}>Nessuna riga.</IonNote>
                    ) : (
                      detail.lines.map((l, i) => (
                        <IonItem key={i}>
                          <IonLabel>
                            {l.description}
                            <p style={{ fontSize: 12 }}>{SUPPLY_REQUEST_MEAL_SERVICE_LABELS[l.meal_service]}{l.menu_label ? ` · menù ${l.menu_label}` : ''}</p>
                          </IonLabel>
                          <IonNote slot="end" style={{ fontWeight: 700, fontSize: 16 }}>{l.qty}</IonNote>
                        </IonItem>
                      ))
                    )}
                  </>
                )}

                {detail.attachment_paths.length > 0 && (
                  <>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '14px 4px 4px', textTransform: 'uppercase' }}>Foto allegate</h3>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {detail.attachment_paths.map((p) =>
                        signed[p] ? (
                          <a key={p} href={signed[p]} target="_blank" rel="noreferrer">
                            <img src={signed[p]} alt="allegato" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0' }} />
                          </a>
                        ) : (
                          <div key={p} style={{ width: 96, height: 96, borderRadius: 10, background: '#e2e8f0' }} />
                        ),
                      )}
                    </div>
                  </>
                )}

                {detail.notes && (
                  <>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '14px 4px 4px', textTransform: 'uppercase' }}>Note</h3>
                    <p style={{ fontSize: 14, color: '#334155', padding: '0 4px' }}>{detail.notes}</p>
                  </>
                )}

                <div style={{ marginTop: 24 }}>
                  {detail.status !== 'submitted' && (
                    <IonButton expand="block" onClick={() => { setDetail(null); history.push('/warehouse/production-plan'); }}>
                      Vai a Pianifica produzione
                      <IonIcon icon={arrowForward} slot="end" />
                    </IonButton>
                  )}
                  <IonButton expand="block" fill="clear" color="danger" onClick={() => detail && setConfirmArchive(detail)}>
                    <IonIcon icon={trashOutline} slot="start" />
                    Archivia richiesta
                  </IonButton>
                </div>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!confirmArchive}
          header="Archivia richiesta"
          message="Archiviare questa richiesta? Non comparirà più tra quelle attive."
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Archivia', role: 'destructive', handler: () => { if (confirmArchive) void archive(confirmArchive); } },
          ]}
          onDidDismiss={() => setConfirmArchive(null)}
        />

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={1800} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
